import axios from 'axios';
import { environment } from '../../environment';
import { useLogin } from '../login';
import { errorCatcher, useSnackbar } from '../../hooks';

export interface IChecklistInput { key: string; value?: string | null }

export interface IIntakeInventoryItem {
    id: number;
    barcode?: string | null;
}

export interface IIntakeResult {
    /** First created item. Kept for callers that only need one barcode. */
    inventory_item?: IIntakeInventoryItem;
    /** Every stock item created by this intake — one per requested stock_qty. */
    inventory_items?: IIntakeInventoryItem[];
    incident_id?: number | null;
    incident_ids?: number[] | null;
}

export interface IIntakeArgs {
    po_line_id: number | string;
    trackable_item_id: string;
    /**
     * How many physical stock items to receive in this one call. The server
     * creates them in a single transaction, so a partial failure receives none.
     */
    stock_qty?: number;
    /** Units contained WITHIN each stock item (not how many items to create). */
    unit_count?: number;
    defective_stock_qty?: number;
    audit_group_id?: string | null;
    audit_checklist?: IChecklistInput[] | null;
    zone_id?: number | string | null;
    audit_passed: boolean;
}

type UseIntake = [{
    intakePOLineUnit: (args: IIntakeArgs) => Promise<IIntakeResult | null>;
}];

const gqlEndpoint = `${environment.baseUrl}/graphql`;
const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
  const { data } = await axios.post(gqlEndpoint, { query, variables }, { headers: { 'X-Wallmates-Auth': authToken } });
  if (data.errors) throw new Error(data.errors[0]?.message || 'GraphQL error');
  return data.data;
}

export const useIntake = (): UseIntake => {
  const [, authToken, loginFunctions] = useLogin();
  const [, setSnackbar] = useSnackbar();

  const intakePOLineUnit = async (args: IIntakeArgs) => {
    try {
      const q = `mutation($po_line_id: ID!, $trackable_item_id: String!, $stock_qty: Int, $unit_count: Int, $defective_stock_qty: Int, $audit_group_id: String, $audit_checklist: [ChecklistInput!], $zone_id: ID, $audit_passed: Boolean!){
        intakePOLineUnit(po_line_id: $po_line_id, trackable_item_id: $trackable_item_id, stock_qty: $stock_qty, unit_count: $unit_count, defective_stock_qty: $defective_stock_qty, audit_group_id: $audit_group_id, audit_checklist: $audit_checklist, zone_id: $zone_id, audit_passed: $audit_passed){
          inventory_item { id barcode }
          inventory_items { id barcode }
          incident_id
          incident_ids
        }
      }`;
      const data = await graphqlRequest(authToken, q, args);
      return data.intakePOLineUnit as IIntakeResult;
    } catch (e: any) {
      errorCatcher(e, loginFunctions.logout);
      setSnackbar({ show: true, snackbarLevel: 'error', text: e.message || 'Failed to intake unit.' });
      return null;
    }
  };

  return [{ intakePOLineUnit }];
}
